import { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, Search, ShieldAlert } from 'lucide-react';
import { useUser } from '@foundation/altzero_base/base/contextapi/UserContext';
import { useUserRole } from '@foundation/altzero_base/base/hooks/useUserRole';
import { theme } from '../../../theme';
import { AssessmentCard } from '../components/AssessmentCard';
import { AssessmentFiltersComponent } from '../components/AssessmentFilters';
import { AssessmentDetailDrawer } from '../components/AssessmentDetailDrawer';
import { fetchIncidents } from '../services/assessmentHistoryService';
import type { IncidentRecord } from '../../incident-logging/types/incident.types';
import type { AssessmentFilters, CaseStatus, PaginatedResponse } from '../types/assessmentHistory.types';

export const AssessmentHistoryPage = () => {
  const { user } = useUser();
  const { isAdmin } = useUserRole();
  const [searchParams, setSearchParams] = useSearchParams();

  // State
  const [incidents, setIncidents] = useState<(IncidentRecord & { current_status: CaseStatus })[]>([]);
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIncidentId, setSelectedIncidentId] = useState<string | null>(
    searchParams.get('open') ?? null
  );

  const [filters, setFilters] = useState<AssessmentFilters>({
    search: searchParams.get('search') ?? '',
    outcome: (searchParams.get('outcome') as AssessmentFilters['outcome']) ?? 'all',
    status: (searchParams.get('status') as AssessmentFilters['status']) ?? 'all',
    datePreset: (searchParams.get('date') as AssessmentFilters['datePreset']) ?? 'all',
    dateFrom: searchParams.get('from'),
    dateTo: searchParams.get('to'),
    venueId: searchParams.get('venue'),
  });

  // Debounce search
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const [debouncedSearch, setDebouncedSearch] = useState(filters.search);

  useEffect(() => {
    debounceRef.current = setTimeout(() => {
      setDebouncedSearch(filters.search);
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [filters.search]);

  // Admins see all org assessments; normal users only see their own (matched by user_id or metadata email)
  const scopedUserEmail = isAdmin ? undefined : (user?.email ?? undefined);
  const scopedUserId = isAdmin ? undefined : (user?.id ?? undefined);

  // Fetch incidents
  const loadIncidents = useCallback(async (page: number = 1) => {
    setIsLoading(true);
    try {
      const result = await fetchIncidents(
        { ...filters, search: debouncedSearch },
        page,
        20,
        scopedUserEmail,
        scopedUserId,
      );
      setIncidents(result.data);
      setPagination(result.pagination);
    } catch (err) {
      console.error('Failed to load incidents:', err);
    } finally {
      setIsLoading(false);
    }
  }, [filters.outcome, filters.status, filters.datePreset, filters.dateFrom, filters.dateTo, filters.venueId, debouncedSearch, scopedUserEmail, scopedUserId]);

  useEffect(() => {
    loadIncidents(1);
  }, [loadIncidents]);

  // Sync filters to URL params
  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.outcome !== 'all') params.set('outcome', filters.outcome);
    if (filters.status !== 'all') params.set('status', filters.status);
    if (filters.datePreset !== 'all') params.set('date', filters.datePreset);
    if (filters.venueId) params.set('venue', filters.venueId);
    if (selectedIncidentId) params.set('open', selectedIncidentId);
    setSearchParams(params, { replace: true });
  }, [filters, selectedIncidentId, setSearchParams]);

  const handlePageChange = (newPage: number) => {
    loadIncidents(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Skeleton loader — compact row style
  const SkeletonRow = () => (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-lg dark:bg-white/[0.03] bg-white border dark:border-white/[0.06] border-gray-200 animate-pulse">
      <div className="h-3 w-[70px] rounded dark:bg-white/[0.06] bg-gray-200 hidden sm:block" />
      <div className="h-3.5 w-32 rounded dark:bg-white/[0.06] bg-gray-200 flex-1" />
      <div className="h-5 w-16 rounded-full dark:bg-white/[0.06] bg-gray-200" />
      <div className="h-5 w-14 rounded-full dark:bg-white/[0.06] bg-gray-200" />
      <div className="h-3 w-[50px] rounded dark:bg-white/[0.06] bg-gray-200 hidden sm:block" />
    </div>
  );

  return (
    <div className={theme.layout.containerMd}>
      {/* Header */}
      <div className={`mb-6 ${theme.anim.fadeInUp}`}>
        <Link to="/home" className={`${theme.button.back} mb-4 inline-flex`}>
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>
        <p className={`${theme.text.accentLabel} mb-2`}>Incident Log</p>
        <h1 className={`text-3xl font-bold ${theme.text.heading} mb-1.5 tracking-tight`}>
          Assessment History
        </h1>
        <p className={`text-base ${theme.text.body}`}>
          Track and review all sideline assessments
        </p>
      </div>

      {/* Filters */}
      <div className="mb-6 animate-fadeInUp animation-delay-100">
        <AssessmentFiltersComponent
          filters={filters}
          onChange={setFilters}
        />
      </div>

      {/* Results count */}
      <div className="flex items-center justify-between mb-4 animate-fadeInUp animation-delay-200">
        <p className={`text-sm ${theme.text.muted}`}>
          {isLoading ? 'Loading...' : `Showing ${pagination.total} assessment${pagination.total !== 1 ? 's' : ''}`}
        </p>
      </div>

      {/* Incident list */}
      <div className="space-y-1.5 animate-fadeInUp animation-delay-200">
        {isLoading ? (
          <>
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
            <SkeletonRow />
          </>
        ) : incidents.length === 0 ? (
          <div className={`${theme.card.paddedMuted} text-center py-14`}>
            <div className="w-14 h-14 mx-auto mb-4 rounded-2xl dark:bg-white/[0.06] bg-gray-100 flex items-center justify-center">
              {filters.search || filters.outcome !== 'all' || filters.status !== 'all' || filters.datePreset !== 'all' ? (
                <Search className={`w-7 h-7 ${theme.text.muted}`} />
              ) : (
                <ShieldAlert className={`w-7 h-7 ${theme.text.muted}`} />
              )}
            </div>
            <p className={`text-sm font-medium ${theme.text.heading} mb-1`}>
              {filters.search || filters.outcome !== 'all' || filters.status !== 'all' || filters.datePreset !== 'all'
                ? 'No assessments match your filters'
                : 'No assessments recorded yet'}
            </p>
            <p className={`text-xs ${theme.text.muted} mb-4`}>
              {filters.search || filters.outcome !== 'all' || filters.status !== 'all' || filters.datePreset !== 'all'
                ? 'Try adjusting your search or filters'
                : 'Complete your first sideline assessment to start tracking incidents.'}
            </p>
            {!(filters.search || filters.outcome !== 'all' || filters.status !== 'all' || filters.datePreset !== 'all') && (
              <Link
                to="/assessment"
                className={`inline-flex items-center gap-1.5 ${theme.button.primaryTeal} text-sm`}
              >
                Start Assessment
              </Link>
            )}
          </div>
        ) : (
          incidents.map((incident) => (
            <AssessmentCard
              key={incident.id}
              incident={incident}
              onClick={() => setSelectedIncidentId(incident.id)}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8 pb-4">
          <button
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
            className={`flex items-center gap-1 ${theme.button.ghost} disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            <ChevronLeft className="w-4 h-4" /> Previous
          </button>
          <span className={`text-sm ${theme.text.muted}`}>
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.totalPages}
            className={`flex items-center gap-1 ${theme.button.ghost} disabled:opacity-30 disabled:cursor-not-allowed`}
          >
            Next <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Detail Drawer */}
      <AssessmentDetailDrawer
        incidentId={selectedIncidentId}
        currentUserId={user?.id ?? ''}
        canEditStatus={isAdmin || incidents.find((i) => i.id === selectedIncidentId)?.user_id === user?.id}
        onClose={() => setSelectedIncidentId(null)}
      />
    </div>
  );
};
